import React from 'react';

import { Heading, Text, VStack } from '@rtkwlf/fenrir-react';
import { type EditScannerConfigInput } from '@rtkwlf/rendall';
import type { Control, FieldErrors, UseFormSetValue } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { FieldWrapper } from '@packages/components/field-wrapper/field-wrapper';

import { useEnhancedScannerDetails } from '../../../../../../../../hooks/use-enhanced-scanner-details/use-enhanced-scanner-details';
import { TargetMultiSelect } from '../../../../../../../scan-schedules/components/new-scan-schedules/target-multi-select';

import { HostDnsServersSkeleton } from './host-dns-servers-skeleton/host-dns-servers-skeleton';

export const HostDnsServers: React.FC<{
  control: Control<EditScannerConfigInput>;
  errors: FieldErrors<EditScannerConfigInput>;
  setValue: UseFormSetValue<EditScannerConfigInput>;
  getValues?: (name: string) => string[];
}> = ({ control, errors, setValue, getValues }) => {
  const { t } = useTranslation('telemetryScannerConfig');
  const { scannerId = '' } = useParams();
  const { data: scannerDetails, isPending } =
    useEnhancedScannerDetails(scannerId);

  const dnsList = React.useMemo(
    () => scannerDetails?.dnsServers?.map((ip) => ({ label: ip, value: ip })),
    [scannerDetails],
  );

  return (
    <VStack data-testid="host-dns-servers-list" gap="smedium">
      <Heading level="4" paddingY="xsmall">
        {t('hostDnsServers.title')}
      </Heading>
      <Text styledAs="body2" marginBottom="xsmall">
        {t('scannerConfiguration.dnsServers')}:
      </Text>
      <Text styledAs="body5" marginBottom="xsmall">
        {t('scannerConfiguration.dnsServersDescription')}
      </Text>

      {isPending ? (
        <HostDnsServersSkeleton />
      ) : (
        <FieldWrapper
          data-testid="host-dns-servers"
          error={errors.hostIdenServers}
          field={
            <Controller
              control={control}
              name="hostIdenServers"
              render={() => (
                <TargetMultiSelect
                  setValue={(selections) => {
                    setValue('hostIdenServers', selections.sort(), {
                      shouldDirty: true,
                      shouldTouch: true,
                      shouldValidate: true,
                    });
                  }}
                  items={dnsList || []}
                  testId="dns-csv-upload"
                  exportFileName={t(
                    'scannerConfiguration..dnsServerListFilename',
                  )}
                  isDisabled={!scannerDetails?.isConnected}
                  showCsvImportButton
                  isFormDirty={control.getFieldState('hostIdenServers').isDirty}
                  getFieldValues={getValues ? getValues('hostIdenServers') : []}
                />
              )}
            />
          }
        />
      )}
    </VStack>
  );
};
